import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Gift, ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { trackExitIntentClaim, trackWhatsAppClick } from "@/lib/tracking";

const BRAND_GOLD = "#D4A843";
const WHATSAPP_NUMBER = "971545846604";

export function ExitIntentPopup() {
  const { t, lang } = useLanguage();
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (dismissed) return;

    // Check if already shown this session
    const alreadyShown = sessionStorage.getItem("exitPopupShown");
    if (alreadyShown) return;

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 5 && !dismissed) {
        setShow(true);
        sessionStorage.setItem("exitPopupShown", "true");
      }
    };

    // Also show after 45 seconds on mobile (no mouse leave event)
    const mobileTimer = setTimeout(() => {
      const isMobile = window.innerWidth < 768;
      if (isMobile && !dismissed && !sessionStorage.getItem("exitPopupShown")) {
        setShow(true);
        sessionStorage.setItem("exitPopupShown", "true");
      }
    }, 45000);

    // Delay adding listener to avoid immediate trigger
    const addTimer = setTimeout(() => {
      document.addEventListener("mouseleave", handleMouseLeave);
    }, 10000);

    return () => {
      clearTimeout(addTimer);
      clearTimeout(mobileTimer);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [dismissed]);

  const handleClose = () => {
    setShow(false);
    setDismissed(true);
  };

  const handleClaim = () => {
    const msg = lang === "en"
      ? "Hi! I'd like to claim my FREE consultation + 10% discount from the website offer."
      : "مرحباً! أريد الحصول على الاستشارة المجانية + خصم 10% من عرض الموقع.";
    trackExitIntentClaim();
    trackWhatsAppClick("exit_intent", "offer_claim");
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, "_blank");
    handleClose();
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <motion.div
            initial={{ scale: 0.8, opacity: 0, y: 30 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 30 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="relative bg-white w-full max-w-lg shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={handleClose} aria-label="Close popup" className="absolute top-4 right-4 z-10 text-white/70 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>

            {/* Top Section */}
            <div className="relative px-8 pt-10 pb-8 text-center bg-[#2C2417]">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: BRAND_GOLD }}
              >
                <Gift className="w-7 h-7 text-gray-900" />
              </motion.div>
              <p className="font-serif text-3xl text-white font-light mb-2">
                {t("Wait! Don't Miss This", "انتظر! لا تفوّت هذا")}
              </p>
              <p className="text-white/60 text-sm">
                {t("Exclusive offer for website visitors only", "عرض حصري لزوار الموقع فقط")}
              </p>
            </div>

            {/* Offer */}
            <div className="px-8 py-8 text-center">
              <div className="inline-block px-6 py-3 mb-4 border-2 border-dashed" style={{ borderColor: BRAND_GOLD }}>
                <span className="text-xs tracking-[0.2em] uppercase font-bold" style={{ color: BRAND_GOLD }}>
                  {t("FREE CONSULTATION", "استشارة مجانية")}
                </span>
                <span className="text-gray-400 mx-2">+</span>
                <span className="text-xs tracking-[0.2em] uppercase font-bold text-gray-900">
                  {t("10% OFF", "خصم 10%")}
                </span>
              </div>

              <p className="text-gray-600 text-sm mb-6 max-w-xs mx-auto">
                {t(
                  "Book today and get a free consultation with our specialist doctors plus 10% off your first treatment.",
                  "احجز اليوم واحصل على استشارة مجانية مع أطبائنا المتخصصين + خصم 10% على أول علاج."
                )}
              </p>

              <button
                onClick={handleClaim}
                className="w-full py-4 text-gray-900 font-bold text-xs tracking-[0.15em] uppercase hover:brightness-95 transition-all flex items-center justify-center gap-2 mb-3"
                style={{ backgroundColor: BRAND_GOLD }}
              >
                {t("CLAIM MY OFFER NOW", "احصل على العرض الآن")}
                <ArrowRight className="w-4 h-4" />
              </button>

              <button
                onClick={handleClose}
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                {t("No thanks, I'll pay full price", "لا شكراً، سأدفع السعر الكامل")}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
